import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n/I18nProvider';
import { createJsonFetch, loadAccountsTree } from '../accounts/accounts.api';
import type { AccountNode, JsonFetch } from '../accounts/accounts.api';
import { periodLabel as formatPeriodLabel, periodRange } from './periods';
import type { DateRange } from './periods';
import {
  DIM_SLOTS,
  loadAccountAnalysis,
  loadAccountCard,
  loadChessTable,
  loadReportPeriods,
  loadTrialBalance,
} from './reports.api';
import type {
  AccountAnalysisReport,
  AccountCardReport,
  ChessTableReport,
  DimSlot,
  ReportPeriod,
  TrialBalanceReport,
} from './reports.api';
import { AccountAnalysisView } from './AccountAnalysisView';
import { AccountCardView } from './AccountCardView';
import { ChessTableView } from './ChessTableView';
import { TrialBalanceView } from './TrialBalanceView';

export type ReportKind = 'trial-balance' | 'account-card' | 'account-analysis' | 'chess-table';

export interface ReportScreenProps {
  kind: ReportKind;
}

type ReportData =
  | { kind: 'trial-balance'; report: TrialBalanceReport }
  | { kind: 'account-card'; report: AccountCardReport }
  | { kind: 'account-analysis'; report: AccountAnalysisReport }
  | { kind: 'chess-table'; report: ChessTableReport };

interface LeafAccount {
  code: string;
  nameAz: string;
  nameEn: string;
  nameRu: string;
}

/** Account pickers only offer leaf accounts: a group has no ledger of its own. */
export function leafAccounts(tree: readonly AccountNode[]): LeafAccount[] {
  const leaves: LeafAccount[] = [];
  const walk = (nodes: readonly AccountNode[]): void => {
    for (const node of nodes) {
      if (node.isGroup) {
        walk(node.children);
        continue;
      }
      leaves.push({ code: node.code, nameAz: node.nameAz, nameEn: node.nameEn, nameRu: node.nameRu });
    }
  };
  walk(tree);
  return leaves;
}

function currentPeriod(): { year: number; month: number } {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function needsAccount(kind: ReportKind): boolean {
  return kind === 'account-card' || kind === 'account-analysis';
}

async function loadReport(
  fetchJson: JsonFetch,
  kind: ReportKind,
  range: DateRange,
  account: string,
  dim: DimSlot,
): Promise<ReportData> {
  if (kind === 'trial-balance') {
    return { kind, report: await loadTrialBalance(fetchJson, range) };
  }
  if (kind === 'account-card') {
    return { kind, report: await loadAccountCard(fetchJson, { ...range, account }) };
  }
  if (kind === 'account-analysis') {
    return { kind, report: await loadAccountAnalysis(fetchJson, { ...range, account, by: dim }) };
  }
  return { kind, report: await loadChessTable(fetchJson, range) };
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function ReportScreen({ kind }: ReportScreenProps): JSX.Element {
  const { authorizedFetch } = useAuth();
  const { t, language } = useI18n();
  const fetchJson = useMemo(() => createJsonFetch(authorizedFetch), [authorizedFetch]);

  const [periods, setPeriods] = useState<ReportPeriod[] | null>(null);
  const [accountTree, setAccountTree] = useState<AccountNode[] | null>(null);
  const [periodId, setPeriodId] = useState<string | null>(null);
  const [account, setAccount] = useState<string>('');
  const [dim, setDim] = useState<DimSlot>('dim1');
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPickers = useCallback(async (): Promise<void> => {
    try {
      const [nextPeriods, tree] = await Promise.all([
        loadReportPeriods(fetchJson),
        needsAccount(kind) ? loadAccountsTree(fetchJson) : Promise.resolve<AccountNode[]>([]),
      ]);
      setPeriods(nextPeriods);
      setAccountTree(tree);
      const first = nextPeriods[0];
      if (first !== undefined) {
        setPeriodId(first.id);
      }
      const firstLeaf = leafAccounts(tree)[0];
      if (firstLeaf !== undefined) {
        setAccount(firstLeaf.code);
      }
      setError(null);
    } catch (cause) {
      setError(errorText(cause));
      setLoading(false);
    }
  }, [fetchJson, kind]);

  useEffect(() => {
    void loadPickers();
  }, [loadPickers]);

  const selected: ReportPeriod | undefined =
    periods === null ? undefined : periods.find((period) => period.id === periodId);

  const range: DateRange =
    selected === undefined ? periodRange(currentPeriod()) : periodRange(selected);

  const ready = periodId !== null && (!needsAccount(kind) || account !== '');

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadReport(fetchJson, kind, range, account, dim)
      .then((next) => {
        if (!cancelled) {
          setData(next);
          setError(null);
          setLoading(false);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(errorText(cause));
          setData(null);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // `range` is derived from `selected`, which is derived from `periods` and `periodId`.
  }, [fetchJson, kind, periodId, account, dim, ready, range.from, range.to]);

  const monthNames = useMemo(
    () => Array.from({ length: 12 }, (_, index) => t(`reportsMonth${index + 1}`)),
    [t],
  );

  const label = formatPeriodLabel(selected ?? currentPeriod(), monthNames);
  const leaves = accountTree === null ? [] : leafAccounts(accountTree);
  const accountLabel = ((): string => {
    const match = leaves.find((leaf) => leaf.code === account);
    if (match === undefined) {
      return account;
    }
    const title = language === 'en' ? match.nameEn : language === 'ru' ? match.nameRu : match.nameAz;
    return `${match.code} — ${title}`;
  })();

  const picker = (
    <div className="report-screen__filters" data-testid="report-filters">
      <label className="field">
        <span>{t('reportsPeriod')}</span>
        <select
          value={periodId ?? ''}
          data-testid="report-period-picker"
          onChange={(event) => setPeriodId(event.target.value)}
        >
          {periods === null || periods.length === 0 ? <option value="">{label}</option> : null}
          {(periods ?? []).map((period) => (
            <option key={period.id} value={period.id}>
              {`${t(`reportsMonth${period.month}`)} ${period.year}`}
            </option>
          ))}
        </select>
      </label>
      {needsAccount(kind) ? (
        <label className="field">
          <span>{t('reportsAccount')}</span>
          <select
            value={account}
            data-testid="report-account-picker"
            onChange={(event) => setAccount(event.target.value)}
          >
            <option value="">{t('reportsPickAccount')}</option>
            {leaves.map((leaf) => {
              const title = language === 'en' ? leaf.nameEn : language === 'ru' ? leaf.nameRu : leaf.nameAz;
              return (
                <option key={leaf.code} value={leaf.code}>
                  {`${leaf.code} — ${title}`}
                </option>
              );
            })}
          </select>
        </label>
      ) : null}
      {kind === 'account-analysis' ? (
        <label className="field">
          <span>{t('reportsGroupBy')}</span>
          <select
            value={dim}
            data-testid="report-dim-picker"
            onChange={(event) => setDim(event.target.value as DimSlot)}
          >
            {DIM_SLOTS.map((slot, index) => (
              <option key={slot} value={slot}>
                {t(`reportsDim${index + 1}`)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );

  const body = ((): JSX.Element => {
    if (error !== null) {
      return (
        <p className="report-screen__error" data-testid="report-screen-error" role="alert">
          {`${t('reportsError')}: ${error}`}
        </p>
      );
    }
    if (loading || data === null) {
      return (
        <p className="muted" data-testid="report-screen-loading">
          {t('reportsLoading')}
        </p>
      );
    }
    if (data.kind === 'trial-balance') {
      return <TrialBalanceView report={data.report} language={language} periodLabel={label} />;
    }
    if (data.kind === 'account-card') {
      return (
        <AccountCardView
          report={data.report}
          language={language}
          periodLabel={label}
          accountLabel={accountLabel}
        />
      );
    }
    if (data.kind === 'account-analysis') {
      return (
        <AccountAnalysisView
          report={data.report}
          language={language}
          periodLabel={label}
          accountLabel={accountLabel}
        />
      );
    }
    return <ChessTableView report={data.report} language={language} periodLabel={label} />;
  })();

  return (
    <div className="report-screen" data-report={kind} data-testid="report-screen">
      {picker}
      {body}
    </div>
  );
}
